import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Button,
    type ButtonProps,
    Flex,
    Popconfirm,
    Space,
    Typography,
    Upload,
    type UploadProps,
} from 'antd';

import { ViewerAvatar } from '@/entities/viewer';

import { useAntMessage, useDesktopMediaQuery } from '@/shared/lib/hooks';
import type { FileType, RequestOptions } from '@/shared/types/antd';

import {
    useDeleteAvatarMutation,
    useUploadAvatarMutation,
} from '../../api/profile.api';

import cls from './edit-avatar.module.css';

interface EditAvatarProps {
    viewerId: number;
}

const acceptFiles = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/heic',
    'image/heic-sequence',
];

export const EditAvatar = memo((props: EditAvatarProps) => {
    const { viewerId } = props;

    const [isOpened, setIsOpened] = useState(false);

    const isDesktop = useDesktopMediaQuery();
    const message = useAntMessage();
    const { t } = useTranslation('profile-management');

    const [uploadAvatarMutation, { isLoading: isUpdateAvatarLoading }] =
        useUploadAvatarMutation();
    const [deleteAvatarMutation, { isLoading: isDeleteAvatarLoading }] =
        useDeleteAvatarMutation();

    const showPopconfirm = useCallback(() => {
        setIsOpened(true);
    }, []);

    const hidePopconfirm = useCallback(() => {
        setIsOpened(false);
    }, []);

    const deleteAvatar = useCallback(() => {
        deleteAvatarMutation(viewerId);
    }, [viewerId, deleteAvatarMutation]);

    const uploadProps = useMemo<UploadProps>(
        () => ({
            accept: acceptFiles.join(', '),
            beforeUpload: (file: FileType) => {
                const { type, size } = file;

                const isAllowedType = acceptFiles.includes(type);

                if (!isAllowedType) {
                    message.error?.({
                        content: t('uploadAvatarFormatTypeError'),
                    });
                }

                const isAllowedSize = size / 1024 / 1024 < 5;

                if (!isAllowedSize) {
                    message.error?.({
                        content: t('uploadAvatarSizeError'),
                    });
                }

                return isAllowedType && isAllowedSize;
            },
            customRequest: (info: RequestOptions) => {
                uploadAvatarMutation({ id: viewerId, file: info.file });
            },
            showUploadList: false,
        }),
        [viewerId, uploadAvatarMutation, t, message],
    );

    const okButtonProps = useMemo<ButtonProps>(
        () => ({ loading: isDeleteAvatarLoading }),
        [isDeleteAvatarLoading],
    );

    useEffect(() => {
        if (!isDeleteAvatarLoading) {
            hidePopconfirm();
        }
    }, [isDeleteAvatarLoading, hidePopconfirm]);

    return (
        <Space
            align="start"
            className={cls.root}
            size={isDesktop ? 24 : 16}
        >
            <ViewerAvatar size={isDesktop ? 80 : 72} />
            <Flex
                gap={8}
                vertical
            >
                <Space size={isDesktop ? 16 : 8}>
                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <Upload {...uploadProps}>
                        <Button loading={isUpdateAvatarLoading}>
                            {t('uploadButton')}
                        </Button>
                    </Upload>
                    <Popconfirm
                        description={t('deletePopconfirmDescription')}
                        okButtonProps={okButtonProps}
                        onCancel={hidePopconfirm}
                        onConfirm={deleteAvatar}
                        open={isOpened}
                        title={t('deletePopconfirmTitle')}
                    >
                        <Button
                            onClick={showPopconfirm}
                            type="link"
                        >
                            {t('deleteButton')}
                        </Button>
                    </Popconfirm>
                </Space>
                <Typography.Text className={cls.formats}>
                    {t('uploadAvatarAcceptFormats')}
                </Typography.Text>
            </Flex>
        </Space>
    );
});

EditAvatar.displayName = 'EditAvatar';
